import { existsSync, mkdirSync } from 'fs';
import { readdir, rm, writeFile } from 'fs/promises';
import { basename, join, relative, resolve, sep } from 'path';
import { Glob } from 'bun';
import { toPascal } from '../utils/stringModifiers';

const indexContentCache = new Map<string, string>();

const resolveDevClientDir = () => {
	const projectRoot = process.cwd();
	const fromSource = resolve(import.meta.dir, '../dev/client');

	// Only use the source path if it exists AND is within the project root
	// (i.e., we're developing absolutejs itself, not using it as a dependency)
	if (existsSync(fromSource) && fromSource.startsWith(projectRoot)) {
		return fromSource;
	}

	// When running from a published npm package, use the installed copy
	const fromNodeModules = resolve(
		projectRoot,
		'node_modules/@absolutejs/absolute/dist/dev/client'
	);
	if (existsSync(fromNodeModules)) return fromNodeModules;

	return resolve(import.meta.dir, './dev/client');
};

const devClientDir = resolveDevClientDir();

const errorOverlayPath = join(devClientDir, 'errorOverlay.ts').replace(
	/\\/g,
	'/'
);

const hmrClientPath = join(devClientDir, 'hmrClient.ts').replace(/\\/g, '/');

const refreshSetupPath = join(devClientDir, 'reactRefreshSetup.ts').replace(
	/\\/g,
	'/'
);

// Absolute path to the vendored react-refresh runtime (shipped inside this
// package's dev client). Bun's reactFastRefresh injects a bare
// `react-refresh/runtime` import into every component during the bundled
// build; the React client build resolves that specifier to this file so
// consumers never need react-refresh in their own node_modules. Matches the
// path reactRefreshSetup's relative `./vendor/reactRefreshRuntime.js` import
// resolves to, so Bun dedupes both into a single runtime instance.
export const reactRefreshRuntimePath = join(
	devClientDir,
	'vendor',
	'reactRefreshRuntime.js'
).replace(/\\/g, '/');

export const generateReactIndexFiles = async (
	reactPagesDirectory: string,
	reactIndexesDirectory: string,
	isDev = false
) => {
	if (!existsSync(reactIndexesDirectory)) {
		mkdirSync(reactIndexesDirectory, { recursive: true });
	}

	const CONVENTION_RE = /^(?:(.+)\.)?(error|loading|not-found)\.[^.]+$/;
	const pagesGlob = new Glob('*.{jsx,tsx}');
	const files: string[] = [];
	for await (const file of pagesGlob.scan({ cwd: reactPagesDirectory })) {
		if (CONVENTION_RE.test(file)) continue;
		files.push(file);
	}

	// Remove stale indexes whose source pages no longer exist
	const currentPageNames = new Set(
		files.map((file) => basename(file).split('.')[0])
	);

	const emptyStringArray: string[] = [];

	const existingIndexes = await readdir(reactIndexesDirectory).catch(
		() => emptyStringArray
	);

	const staleIndexes = existingIndexes.filter((indexFile) => {
		const indexName = indexFile.replace(/\.tsx$/, '');

		return indexName !== '_refresh' && !currentPageNames.has(indexName);
	});

	if (staleIndexes.length > 0) {
		await Promise.all(
			staleIndexes.map((indexFile) => {
				indexContentCache.delete(
					join(reactIndexesDirectory, indexFile)
				);

				return rm(join(reactIndexesDirectory, indexFile), {
					force: true
				});
			})
		);
	}

	// Compute relative path from indexes dir to pages dir for imports
	const pagesRelPath = relative(
		resolve(reactIndexesDirectory),
		resolve(reactPagesDirectory)
	)
		.split(sep)
		.join('/');

	const promises = files.map(async (file) => {
		const fileName = basename(file);
		const componentName = fileName.split('.')[0]!;
		const pascalComponentName = toPascal(componentName);

		const hmrPreamble = isDev
			? [
					`window.__HMR_FRAMEWORK__ = "react";`,
					`window.__REACT_COMPONENT_KEY__ = "${componentName}Index";`,
					`import '${refreshSetupPath}';`,
					`import '${hmrClientPath}';`,
					`import { showErrorOverlay, hideErrorOverlay } from '${errorOverlayPath}';\n`
				]
			: [];

		const reactImports = isDev
			? [
					`import { hydrateRoot, createRoot } from 'react-dom/client';`,
					`import { createElement, Component } from 'react';`
				]
			: [
					`import { hydrateRoot, createRoot } from 'react-dom/client';`,
					`import { createElement } from 'react';`
				];

		const errorBoundaryDef = isDev
			? [
					`\n// Dev-only Error Boundary to catch React render errors`,
					`class ErrorBoundary extends Component {`,
					`\tconstructor(props) {`,
					`\t\tsuper(props);`,
					`\t\tthis.state = { hasError: false };`,
					`\t\twindow.__ERROR_BOUNDARY__ = this;`,
					`\t}`,
					`\tstatic getDerivedStateFromError() {`,
					`\t\treturn { hasError: true };`,
					`\t}`,
					`\tcomponentDidCatch(error) {`,
					`\t\tshowErrorOverlay({`,
					`\t\t\tframework: 'react',`,
					`\t\t\tkind: 'runtime',`,
					`\t\t\tmessage: error && error.stack ? error.stack : String(error)`,
					`\t\t});`,
					`\t}`,
					`\tcomponentDidUpdate(prevProps, prevState) {`,
					`\t\tif (prevState.hasError && !this.state.hasError) {`,
					`\t\t\thideErrorOverlay();`,
					`\t\t}`,
					`\t}`,
					`\treset() {`,
					`\t\tthis.setState({ hasError: false });`,
					`\t}`,
					`\trender() {`,
					`\t\tif (this.state.hasError) return null;`,
					``,
					`\t\treturn this.props.children;`,
					`\t}`,
					`}\n`
				]
			: [];

		const content = [
			...hmrPreamble,
			...reactImports,
			`import * as PageModule from '${pagesRelPath}/${componentName}';\n`,
			...errorBoundaryDef,
			`// Hydration with error handling and fallback`,
			`const isDev = ${isDev};`,
			`const componentPath = '${pagesRelPath}/${componentName}';\n`,
			`function resolvePageComponent(module, candidateNames) {`,
			`\tfor (const name of candidateNames) {`,
			`\t\tconst value = module[name];`,
			`\t\tif (typeof value === 'function' || (value && typeof value === 'object')) return value;`,
			`\t}`,
			`\tfor (const [name, value] of Object.entries(module)) {`,
			`\t\tif (!/^[A-Z]/.test(name)) continue;`,
			`\t\tif (typeof value === 'function' || (value && typeof value === 'object')) return value;`,
			`\t}`,
			`\tthrow new Error('React page module ' + componentPath + ' does not export a component. Expected default, ${pascalComponentName}, ${componentName}, or any PascalCase export.');`,
			`}`,
			`const PageComponent = resolvePageComponent(PageModule, ['default', '${pascalComponentName}', '${componentName}']);\n`,
			`function isHydrationError(error) {`,
			`\tif (!error) return false;`,
			`\tconst errorMessage = error instanceof Error ? error.message : String(error);`,
			`\tconst errorString = String(error);`,
			`\tconst fullMessage = errorMessage + ' ' + errorString;`,
			`\tconst hydrationKeywords = ['hydration', 'Hydration', 'mismatch', 'Mismatch', 'did not match', 'server rendered HTML', 'server HTML', 'client HTML', 'Hydration failed'];`,
			`\tconst isHydration = hydrationKeywords.some(keyword => fullMessage.includes(keyword));`,
			`\t`,
			`\t// Ignore whitespace-only mismatches in <head> - these are harmless formatting differences`,
			`\t// The error often shows: + <link...> vs - {"\\n            "} which is just formatting`,
			`\tif (isHydration) {`,
			`\t\t// Check if this is a head/link/stylesheet related mismatch`,
			`\t\tconst isHeadRelated = fullMessage.includes('<head') || fullMessage.includes('</head>') || fullMessage.includes('head>') || fullMessage.includes('<link') || fullMessage.includes('link>') || fullMessage.includes('stylesheet') || fullMessage.includes('fonts.googleapis') || fullMessage.includes('rel="stylesheet"');`,
			`\t\t`,
			`\t\t// Check if the mismatch involves only whitespace/newlines`,
			`\t\t// Pattern: looks for {"\\n"} or {"\\n            "} or similar whitespace-only content`,
			`\t\t// Also check for patterns like: - {"\\n            "} or + <link...>`,
			`\t\tconst hasWhitespacePattern = /\\{\\s*["']\\\\n[^"']*["']\\s*\\}/.test(fullMessage) || /\\{\\s*["'][\\\\n\\\\r\\\\s]+["']\\s*\\}/.test(fullMessage) || /-\\s*\\{\\s*["'][\\\\n\\\\r\\\\s]+["']\\s*\\}/.test(fullMessage);`,
			`\t\tconst isWhitespaceOnly = /^[\\s\\n\\r]*$/.test(errorString) || /^[\\s\\n\\r]*$/.test(errorMessage);`,
			`\t\tconst hasNewlinePattern = fullMessage.includes('\\\\n') || fullMessage.includes('\\\\r') || fullMessage.includes('\\n') || fullMessage.includes('\\r');`,
			`\t\t`,
			`\t\t// If it's head-related and involves whitespace/newlines, ignore it`,
			`\t\tif (isHeadRelated && (hasWhitespacePattern || isWhitespaceOnly || hasNewlinePattern)) {`,
			`\t\t\treturn false; // Don't treat whitespace-only head mismatches as errors`,
			`\t\t}`,
			`\t}`,
			`\treturn isHydration;`,
			`}\n`,
			`function logHydrationError(error, componentName) {`,
			`\tif (!isDev) return;`,
			`\tif (window.__HMR_WS__ && window.__HMR_WS__.readyState === WebSocket.OPEN) {`,
			`\t\ttry {`,
			`\t\t\twindow.__HMR_WS__.send(JSON.stringify({`,
			`\t\t\t\ttype: 'hydration-error',`,
			`\t\t\t\tdata: {`,
			`\t\t\t\t\tcomponentName: '${componentName}',`,
			`\t\t\t\t\tcomponentPath: componentPath,`,
			`\t\t\t\t\terror: error instanceof Error ? error.message : String(error),`,
			`\t\t\t\t\ttimestamp: Date.now()`,
			`\t\t\t\t}`,
			`\t\t\t}));`,
			`\t\t} catch (err) {}`,
			`\t}`,
			`}\n`,
			`// Track if we've already switched to client-only mode`,
			`let hasSwitchedToClientOnly = false;`,
			`let hydrationErrorDetected = false;\n`,
			`function handleHydrationFallback(error) {`,
			`\tif (hasSwitchedToClientOnly) return; // Already handled`,
			`\thasSwitchedToClientOnly = true;`,
			`\thydrationErrorDetected = true;\n`,
			`\tlogHydrationError(error, '${componentName}');\n`,
			`\t// Fallback: client-only render (no hydration)`,
			`\ttry {`,
			`\t\t// Unmount existing root if it exists`,
			`\t\tif (window.__REACT_ROOT__ && typeof window.__REACT_ROOT__.unmount === 'function') {`,
			`\t\t\ttry {`,
			`\t\t\twindow.__REACT_ROOT__.unmount();`,
			`\t\t\t} catch (e) {`,
			`\t\t\t\t// Ignore unmount errors`,
			`\t\t\t}`,
			`\t\t}\n`,
			`\t\t// Render into the same root container when falling back to client-only`,
			`\t\tconst root = createRoot(container);`,
			`\t\troot.render(${isDev ? `createElement(ErrorBoundary, null, createElement(PageComponent, mergedProps))` : `createElement(PageComponent, mergedProps)`});`,
			`\t\twindow.__REACT_ROOT__ = root;`,
			`\t\twindow.__HMR_CLIENT_ONLY_MODE__ = true;`,
			`\t} catch (fallbackError) {`,
			`\t\twindow.location.reload();`,
			`\t}`,
			`}\n`,
			`// HMR State Preservation: Check for preserved state and merge with initial props`,
			`// This allows state to be preserved across HMR updates without modifying component files`,
			`let preservedState = (typeof window !== 'undefined' && window.__HMR_PRESERVED_STATE__) ? window.__HMR_PRESERVED_STATE__ : {};\n`,
			`// Also check sessionStorage for state that survived a page reload (for React HMR)`,
			`if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {`,
			`\tconst hmrStateJson = sessionStorage.getItem('__REACT_HMR_STATE__');`,
			`\tif (hmrStateJson) {`,
			`\t\ttry {`,
			`\t\t\tconst hmrState = JSON.parse(hmrStateJson);`,
			`\t\t\tpreservedState = { ...preservedState, ...hmrState };`,
			`\t\t\tsessionStorage.removeItem('__REACT_HMR_STATE__');`,
			`\t\t} catch (e) {}`,
			`\t}`,
			`}\n`,
			`const mergedProps = { ...(window.__INITIAL_PROPS__ || {}), ...preservedState };`,
			`// Clear preserved state after using it (so it doesn't persist across multiple updates)`,
			`if (typeof window !== 'undefined') {`,
			`\twindow.__HMR_PRESERVED_STATE__ = undefined;`,
			`}\n`,
			`// Attempt hydration with error handling`,
			`// Use document (not document.body) when the page renders <html><head><body>`,
			`// to avoid "In HTML, <html> cannot be a child of <body>" hydration error`,
			`const container = typeof document !== 'undefined' ? document : null;`,
			`if (!container) {`,
			`\tthrow new Error('React root container not found: document is null');`,
			`}\n`,
			`// Guard: only hydrate on first load. During HMR re-imports, skip hydration`,
			`// so React Fast Refresh can swap components in-place and preserve state.`,
			`if (!window.__REACT_ROOT__) {`,
			`\tlet root;`,
			`\t// After HMR, SSR is skipped to avoid stale content flash — render client-only`,
			`\tif (window.__SSR_DIRTY__) {`,
			`\t\troot = createRoot(container);`,
			`\t\troot.render(${isDev ? `createElement(ErrorBoundary, null, createElement(PageComponent, mergedProps))` : `createElement(PageComponent, mergedProps)`});`,
			`\t\twindow.__REACT_ROOT__ = root;`,
			`\t} else {`,
			`\ttry {`,
			`\t\t// Use onRecoverableError to catch hydration errors (React 19)`,
			`\t\troot = hydrateRoot(`,
			`\t\t\tcontainer,`,
			`\t\t\t${isDev ? `createElement(ErrorBoundary, null, createElement(PageComponent, mergedProps))` : `createElement(PageComponent, mergedProps)`},`,
			`\t\t\t{`,
			`\t\t\t\tonRecoverableError: (error) => {`,
			`\t\t\t\t\t// Check if this is a hydration error (isHydrationError filters out whitespace-only head mismatches)`,
			`\t\t\t\t\tif (isDev && isHydrationError(error)) {`,
			`\t\t\t\t\t\t// Real hydration error - handle it`,
			`\t\t\t\t\t\thandleHydrationFallback(error);`,
			`\t\t\t\t\t} else {`,
			`\t\t\t\t\t\t// Not a hydration error, or it's a whitespace-only mismatch that was filtered out`,
			`\t\t\t\t\t\t// Check if it's a whitespace-only head mismatch using the same logic as isHydrationError`,
			`\t\t\t\t\t\tconst errorMessage = error instanceof Error ? error.message : String(error);`,
			`\t\t\t\t\t\tconst errorString = String(error);`,
			`\t\t\t\t\t\tconst fullMessage = errorMessage + ' ' + errorString;`,
			`\t\t\t\t\t\tconst hydrationKeywords = ['hydration', 'Hydration', 'mismatch', 'Mismatch', 'did not match', 'server rendered HTML', 'server HTML', 'client HTML', 'Hydration failed'];`,
			`\t\t\t\t\t\tconst isHydration = hydrationKeywords.some(keyword => fullMessage.includes(keyword));`,
			`\t\t\t\t\t\tif (isHydration) {`,
			`\t\t\t\t\t\t\t// Check if this is a head/link/stylesheet related mismatch`,
			`\t\t\t\t\t\t\tconst isHeadRelated = fullMessage.includes('<head') || fullMessage.includes('</head>') || fullMessage.includes('head>') || fullMessage.includes('<link') || fullMessage.includes('link>') || fullMessage.includes('stylesheet') || fullMessage.includes('fonts.googleapis') || fullMessage.includes('rel="stylesheet"');`,
			`\t\t\t\t\t\t\t// Check if the mismatch involves only whitespace/newlines`,
			`\t\t\t\t\t\t\tconst hasWhitespacePattern = /\\{\\s*["']\\\\n[^"']*["']\\s*\\}/.test(fullMessage) || /\\{\\s*["'][\\\\n\\\\r\\\\s]+["']\\s*\\}/.test(fullMessage) || /-\\s*\\{\\s*["'][\\\\n\\\\r\\\\s]+["']\\s*\\}/.test(fullMessage);`,
			`\t\t\t\t\t\t\tconst isWhitespaceOnly = /^[\\s\\n\\r]*$/.test(errorString) || /^[\\s\\n\\r]*$/.test(errorMessage);`,
			`\t\t\t\t\t\t\tconst hasNewlinePattern = fullMessage.includes('\\\\n') || fullMessage.includes('\\\\r') || fullMessage.includes('\\n') || fullMessage.includes('\\r');`,
			`\t\t\t\t\t\t\t// If it's head-related and involves whitespace/newlines, silently ignore it`,
			`\t\t\t\t\t\t\tif (isHeadRelated && (hasWhitespacePattern || isWhitespaceOnly || hasNewlinePattern)) {`,
			`\t\t\t\t\t\t\t\t// Already logged by isHydrationError, just return silently`,
			`\t\t\t\t\t\t\t\treturn;`,
			`\t\t\t\t\t\t\t}`,
			`\t\t\t\t\t\t}`,
			`\t\t\t\t\t\t// Log other recoverable errors`,
			`\t\t\t\t\t\tconsole.error('React recoverable error:', error);`,
			`\t\t\t\t\t}`,
			`\t\t\t\t}`,
			`\t\t\t}`,
			`\t\t);`,
			`\t\twindow.__REACT_ROOT__ = root;`,
			`\t} catch (error) {`,
			`\t\t// Catch synchronous errors (shouldn't happen with hydrateRoot, but safety net)`,
			`\t\tif (isDev && isHydrationError(error)) {`,
			`\t\t\thandleHydrationFallback(error);`,
			`\t\t} else {`,
			`\t\t\tthrow error;`,
			`\t\t}`,
			`\t}`,
			`\t} // end else (normal hydration path)\n`,
			`\t// Also listen for hydration errors via console.error (React logs them there)`,
			`\tif (isDev) {`,
			`\t\tconst originalError = console.error;`,
			`\t\tconsole.error = function(...args) {`,
			`\t\t\tconst errorMessage = args.map(arg => {`,
			`\t\t\t\tif (arg instanceof Error) return arg.message;`,
			`\t\t\t\treturn String(arg);`,
			`\t\t\t}).join(' ');`,
			`\t\t\t`,
			`\t\t\t// Check if this is a hydration error`,
			`\t\t\tif (isHydrationError({ message: errorMessage }) && !hydrationErrorDetected) {`,
			`\t\t\t\thydrationErrorDetected = true;`,
			`\t\t\t\t// Create a synthetic error for fallback`,
			`\t\t\t\tconst syntheticError = new Error(errorMessage);`,
			`\t\t\t\t// Use setTimeout to ensure this happens after React's error handling`,
			`\t\t\t\tsetTimeout(() => {`,
			`\t\t\t\t\thandleHydrationFallback(syntheticError);`,
			`\t\t\t\t}, 0);`,
			`\t\t\t}`,
			`\t\t\t`,
			`\t\t\t// Call original console.error`,
			`\t\t\toriginalError.apply(console, args);`,
			`\t\t};`,
			`\t}`,
			`}`,
			...(isDev
				? [
						`\n// Pre-warm: import the page module from the module server`,
						`// immediately so the browser caches all /@src/ URLs.`,
						`import('/@src/${relative(process.cwd(), resolve(reactPagesDirectory, `${componentName}.tsx`)).replace(/\\/g, '/')}').catch(() => {});`
					]
				: [])
		].join('\n');

		const indexPath = join(reactIndexesDirectory, `${componentName}.tsx`);
		const hasher = new Bun.CryptoHasher('md5');
		hasher.update(content);
		const contentHash = hasher.digest('hex');

		if (
			indexContentCache.get(indexPath) === contentHash &&
			existsSync(indexPath)
		) {
			return;
		}

		indexContentCache.set(indexPath, contentHash);
		await writeFile(indexPath, content);
	});
	await Promise.all(promises);

	// Generate a dummy entry that imports React so code splitting extracts
	// React into a shared chunk. This lets HMR re-import component entries
	// without creating a duplicate React instance.
	// IMPORTANT: reactRefreshSetup MUST be imported BEFORE react so that
	// injectIntoGlobalHook patches window.__REACT_DEVTOOLS_GLOBAL_HOOK__
	// before React initializes and checks it. Without this, the Refresh
	// Runtime can't reach React's reconciler and performReactRefresh()
	// silently does nothing.
	if (!isDev) {
		return;
	}

	const refreshPath = join(reactIndexesDirectory, '_refresh.tsx');

	if (!existsSync(refreshPath)) {
		await writeFile(
			refreshPath,
			`import '${refreshSetupPath}';\nimport 'react';\nimport 'react-dom/client';\n`
		);
	}
};
